import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as ArrowLeftIcon } from "assets/svgs/ArrowLeft.svg";
import type { Action } from "app/services/types.generated";
import { getMission } from "lib/fleetMissionStorage/FleetMissionStorageClient";
import type { OrgMissionDetails } from "lib/orgServer/OrgServerClient";
import { formatLocalDateTime, LOCAL_DATETIME_MEDIUM_SHORT } from "lib/utils/dateTimeDisplay";
import { useOrgFleet } from "providers/OrgFleetProvider";
import ActionList from "./ActionList";

interface ReadOnlyMissionSubpageProps {
  missionId?: string;
  sourceArcSerial?: string;
  showBackButton?: boolean;
  onBack: () => void;
}

const ReadOnlyMissionSubpage = ({
  missionId = "",
  sourceArcSerial,
  showBackButton = true,
  onBack,
}: ReadOnlyMissionSubpageProps) => {
  const { t } = useTranslation(["missions", "common"]);
  const proxyHostname = useOrgFleet()?.proxyHostname ?? null;
  const [mission, setMission] = useState<OrgMissionDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setMission(null);
    if (!sourceArcSerial || !missionId || !proxyHostname) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getMission(proxyHostname, sourceArcSerial, missionId)
      .then((value) => {
        if (!cancelled) setMission(value);
      })
      .catch(() => {
        if (!cancelled) setMission(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceArcSerial, missionId, proxyHostname]);

  const actions = (mission?.actions ?? []).map((action) => ({
    id: action.sourceActionId,
    missionId: action.sourceMissionId,
    actionType: action.actionType,
    sequenceOrder: action.sequenceOrder,
    name: action.name,
    note: action.note,
    args: action.args,
    createdAt: action.createdAt,
  })) as Action[];

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-r10 bg-black90">
      <div className="flex shrink-0 items-center border-b border-black80 px-4 pb-3 pt-4">
        {showBackButton && (
          <FunctionalButton icon={<ArrowLeftIcon className="w-6" />} className="h-6 w-6 shrink-0" onClick={onBack} />
        )}
        {mission && <h2 className={`${showBackButton ? "ml-4 " : ""}truncate text-h3`}>{mission.name}</h2>}
      </div>
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center p-4 text-h4 text-black60">{t("subpage.loading")}</div>
      ) : !mission ? (
        <div className="flex flex-1 items-center justify-center p-4 text-h4 text-red-500">{t("subpage.notFound")}</div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4">
          <div className="mb-4 rounded bg-black80 p-3">
            <p className="text-h5 text-black60">{t("subpage.created", { datetime: formatLocalDateTime(mission.createdAt, LOCAL_DATETIME_MEDIUM_SHORT) })}</p>
            <p className="text-h5 text-black60">{t("subpage.actionCount", { count: actions.length })}</p>
          </div>
          <ActionList actions={actions} onNoteChange={() => {}} readOnly />
        </div>
      )}
    </div>
  );
};

export default ReadOnlyMissionSubpage;
